import React, { useState } from "react";
import { Building, Briefcase, Users, Search, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const industries = ["Technology", "Finance", "Healthcare", "Education", "Manufacturing", "Retail"];
const roles = ["Software Engineer", "Product Manager", "Data Scientist", "UI/UX Designer", "DevOps Engineer"];
const levels = ["Entry Level", "Junior", "Mid Level", "Senior", "Lead/Manager", "Executive"];

const NewAssessment = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  const [selectedIndustry, setSelectedIndustry] = useState("Technology");
  const [selectedRole, setSelectedRole] = useState("Software Engineer");
  const [selectedLevel, setSelectedLevel] = useState("Entry Level");

  const [industrySearch, setIndustrySearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [levelSearch, setLevelSearch] = useState("");

  const filteredIndustries = industries.filter(i => i.toLowerCase().includes(industrySearch.toLowerCase()));
  const filteredRoles = roles.filter(r => r.toLowerCase().includes(roleSearch.toLowerCase()));
  const filteredLevels = levels.filter(l => l.toLowerCase().includes(levelSearch.toLowerCase()));

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Create New Assessment</h1>
          <p className="text-sm text-gray-600 mt-1">
            {step === 1
              ? "Select position details for your assessment"
              : "Choose assessments for this position"}
          </p>
        </div>
        <span className="text-sm text-gray-500">Step {step} of 2</span>
      </div>

      {step === 1 ? (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Industry */}
            <div>
              <h3 className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Building className="w-4 h-4 mr-2 text-violet-600" />
                Industry
              </h3>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search industries..."
                  className="w-full border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  value={industrySearch}
                  onChange={(e) => setIndustrySearch(e.target.value)}
                />
              </div>
              <div className="border rounded-md overflow-hidden">
                {filteredIndustries.map((ind) => (
                  <div
                    key={ind}
                    className={`px-4 py-2 text-sm cursor-pointer border-b last:border-none hover:bg-violet-50 ${selectedIndustry === ind
                      ? "bg-violet-50 font-medium border-l-4 border-violet-600"
                      : ""
                      }`}
                    onClick={() => setSelectedIndustry(ind)}
                  >
                    <span className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-gray-400" />
                      {ind}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Role */}
            <div>
              <h3 className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Briefcase className="w-4 h-4 mr-2 text-violet-600" />
                Role
              </h3>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search roles..."
                  className="w-full border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  value={roleSearch}
                  onChange={(e) => setRoleSearch(e.target.value)}
                />
              </div>
              <div className="border rounded-md overflow-hidden">
                {filteredRoles.map((role) => (
                  <div
                    key={role}
                    className={`px-4 py-2 text-sm cursor-pointer border-b last:border-none hover:bg-violet-50 ${selectedRole === role
                      ? "bg-violet-50 font-medium border-l-4 border-violet-600"
                      : ""
                      }`}
                    onClick={() => setSelectedRole(role)}
                  >
                    {role}
                  </div>
                ))}
                <div
                  className="px-4 py-2 text-sm text-violet-600 cursor-pointer flex items-center gap-1 hover:underline"
                  onClick={() => alert("Add custom role clicked")}
                >
                  <Plus className="w-4 h-4" /> Add Custom Role
                </div>
              </div>
            </div>

            {/* Level */}
            <div>
              <h3 className="flex items-center text-sm font-medium text-gray-700 mb-2">
                <Users className="w-4 h-4 mr-2 text-violet-600" />
                Level
              </h3>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search levels..."
                  className="w-full border rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  value={levelSearch}
                  onChange={(e) => setLevelSearch(e.target.value)}
                />
              </div>
              <div className="border rounded-md overflow-hidden">
                {filteredLevels.map((level) => (
                  <div
                    key={level}
                    className={`px-4 py-2 text-sm cursor-pointer border-b last:border-none hover:bg-violet-50 ${selectedLevel === level
                      ? "bg-violet-50 font-medium border-l-4 border-violet-600"
                      : ""
                      }`}
                    onClick={() => setSelectedLevel(level)}
                  >
                    {level}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={() => navigate("/assessments")}
              className="px-4 py-2 text-sm text-gray-700 border rounded-md hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={() => setStep(2)}
              className="px-5 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-md"
            >
              Choose Assessments →
            </button>
          </div>
        </div>
      ) : (
        // STEP 2 - Assessments Coming Soon
        <div className="bg-white rounded-lg shadow-sm p-6 relative">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="md:col-span-2 space-y-3">
              <h2 className="text-lg font-semibold text-gray-800">
                Position Summary
              </h2>

              <div className="flex items-start gap-2 text-sm">
                <span className="text-violet-600">🏢</span>
                <div>
                  <span className="text-gray-600">Industry: </span>
                  <span className="font-medium text-gray-800">
                    {selectedIndustry}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2 text-sm">
                <span className="text-violet-600">💼</span>
                <div>
                  <span className="text-gray-600">Role: </span>
                  <span className="font-medium text-gray-800">
                    {selectedRole}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-2 text-sm">
                <span className="text-violet-600">👤</span>
                <div>
                  <span className="text-gray-600">Level: </span>
                  <span className="font-medium text-gray-800">
                    {selectedLevel}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-violet-50 rounded-md p-4">
              <h3 className="text-sm font-semibold text-violet-700 mb-2">
                Assessment Progress
              </h3>
              <div className="w-full h-2 bg-violet-200 rounded-full mb-2">
                <div className="h-2 bg-violet-600 rounded-full w-2/5"></div>
              </div>
              <p className="text-xs text-gray-600">
                Complete the assessment setup to publish your test
              </p>
            </div>
          </div>

          <div className="text-center mb-10">
            <h2 className="text-violet-700 text-lg font-semibold">
              Assessments Selection Coming Soon
            </h2>
            <p className="text-sm text-gray-600 mt-2 max-w-xl mx-auto">
              This part of the feature is currently under development. In the
              future, you'll be able to select from a variety of assessment
              types for your position.
            </p>
          </div>

          <div className="flex justify-center gap-4">
            <button
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => setStep(1)}
            >
              ← Back
            </button>
            <button
              className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-sm font-medium"
              onClick={() => navigate("/assessments")}
            >
              Review & Publish →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewAssessment;
